import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import type { FeatureCollection } from 'geojson'
import type * as maplibregl from 'maplibre-gl'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getLayerConfigs } from './layer-config'
import { useOverwatchMapOperatingAreasQuery } from './OperatingAreas.hook'
import {
  type AreaFeature,
  buildOutsideMask,
  buildUnshadedArea,
  mergeOperatingAreas,
  mergeOutlineFeatures,
} from './operatingAreas'
import { resolveRegionId } from './roadBlockagesUtils'

// Two mask layers take turns, so a changed shade fades in over the old one.
const MASKS = [0, 1].map(index => ({
  source: `operating-areas-mask-source-${index}`,
  layer: `operating-areas-mask-fill-${index}`,
}))
const OUTLINE_SOURCE_ID = 'operating-areas-outline-source'
/** Exported so the geonet z-ordering can keep this outline above geonet outlines. */
export const OPERATING_AREA_OUTLINE_LAYER_ID = 'operating-areas-outline'

// Same look as the Backoffice fleet map in light mode: a light grey wash over
// everything outside the operating area and a thin black outline around it.
const OUTLINE_COLOR = '#000000'
const MASK_COLOR = '#000000'
const MASK_OPACITY = 0.1
const OUTLINE_WIDTH = 1.5
const MASK_FADE_MS = 300
// A new mask is usually re-tiled within a few frames; never wait longer than
// this before fading it in.
const MASK_TILE_WAIT_MS = 500
const EMPTY_GEOJSON: FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
}

// A failed live query falls back to the hardcoded outline instead of showing
// an error notification.
const ignoreError = () => {}

const removeLayers = (map: maplibregl.Map) => {
  for (const id of [
    OPERATING_AREA_OUTLINE_LAYER_ID,
    ...MASKS.map(mask => mask.layer),
  ]) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  for (const id of [OUTLINE_SOURCE_ID, ...MASKS.map(mask => mask.source)]) {
    if (map.getSource(id)) map.removeSource(id)
  }
}

// Calls `onLoaded` once the source's tiles carry its new data, so the fade
// doesn't run before there is anything to show. Returns a cancel function.
const whenSourceRetiled = (
  map: maplibregl.Map,
  sourceId: string,
  onLoaded: () => void,
) => {
  let timeout: ReturnType<typeof setTimeout> | undefined
  const onSourceData = (event: maplibregl.MapSourceDataEvent) => {
    if (event.sourceId !== sourceId || !event.tile) return
    if (map.isSourceLoaded(sourceId)) finish()
  }
  const cancel = () => {
    clearTimeout(timeout)
    map.off('sourcedata', onSourceData)
  }
  const finish = () => {
    cancel()
    onLoaded()
  }
  map.on('sourcedata', onSourceData)
  timeout = setTimeout(finish, MASK_TILE_WAIT_MS)
  return cancel
}

/**
 * The region's operating area. It comes from `operatingAreasByRegionId`, the
 * same data the Backoffice fleet map uses, so both apps show the same outline.
 * When the region is unknown, or the query fails or returns no area, the
 * metro's hardcoded geonet outline is used instead.
 *
 * `isLive` tells whether the live area was found; `settled` turns true once
 * that is known.
 */
export const useOperatingAreas = ({
  metropolisId,
  metropolisName,
  enabled,
}: {
  metropolisId: number
  metropolisName?: string
  enabled: boolean
}) => {
  const regionId = resolveRegionId(metropolisName)

  const { data, error } = useErrorHandlingQuery(
    useOverwatchMapOperatingAreasQuery,
    enabled && regionId
      ? { variables: { regionId }, onError: ignoreError }
      : { skip: true },
  )

  const liveArea = useMemo(
    () => mergeOperatingAreas(data?.operatingAreasByRegionId ?? []),
    [data],
  )
  const settled =
    !enabled || !regionId || error !== undefined || data !== undefined
  const needsFallback = enabled && settled && !liveArea

  const fallbackConfig = getLayerConfigs(metropolisId, metropolisName).find(
    config => config.type === 'geonet',
  )
  const [fallback, setFallback] = useState<{
    configId: string
    outline: FeatureCollection
  } | null>(null)

  useEffect(() => {
    if (!needsFallback || !fallbackConfig) return
    let cancelled = false
    void fallbackConfig.loader().then(outline => {
      if (!cancelled) setFallback({ configId: fallbackConfig.id, outline })
    })
    return () => {
      cancelled = true
    }
  }, [needsFallback, fallbackConfig])

  const fallbackOutline =
    needsFallback && fallback?.configId === fallbackConfig?.id
      ? fallback?.outline
      : undefined

  const fallbackArea = useMemo(
    () => (fallbackOutline ? mergeOutlineFeatures(fallbackOutline) : null),
    [fallbackOutline],
  )

  return { area: liveArea ?? fallbackArea, isLive: liveArea !== null, settled }
}

/**
 * Draws the operating area: a black outline around it and a grey mask outside
 * it. Shown hardcoded geonets are cut out of the mask too, so the unshaded part
 * grows to cover them while the outline stays the operating area's.
 */
export const useOperatingAreasLayers = ({
  map,
  area,
  shownGeonetOutlines,
  insertBeforeLayer,
}: {
  map: maplibregl.Map | null
  area: AreaFeature | null
  shownGeonetOutlines: ReadonlyArray<FeatureCollection>
  insertBeforeLayer?: string
}) => {
  const mask = useMemo(() => {
    if (!area) return null
    // Shared with the lane clip, which usually built it already.
    const unshaded = buildUnshadedArea(area, shownGeonetOutlines)
    return unshaded ? buildOutsideMask(unshaded) : null
  }, [area, shownGeonetOutlines])

  // The layers are created once and their data swapped in place below. Removing
  // and re-adding a GeoJSON source leaves it empty until it is re-tiled, which
  // made the shade blink whenever a geonet was toggled.
  const hasArea = area !== null
  // Read when the layers are (re)created, so they start with the current data.
  const dataRef = useRef({ area, mask })
  dataRef.current = { area, mask }
  // Which of the two mask layers is shown, and the mask it shows.
  const visibleMaskRef = useRef(0)
  const shownMaskRef = useRef<AreaFeature | null>(null)
  // Bumped whenever the layers are recreated, so a fade still waiting for the
  // old layers' tiles doesn't run on the new ones.
  const layersGenerationRef = useRef(0)

  useEffect(() => {
    if (!map || !hasArea) return

    const beforeLayer =
      insertBeforeLayer && map.getLayer(insertBeforeLayer)
        ? insertBeforeLayer
        : undefined

    layersGenerationRef.current++
    visibleMaskRef.current = 0
    shownMaskRef.current = dataRef.current.mask
    MASKS.forEach(({ source, layer }, index) => {
      const visible = index === visibleMaskRef.current
      map.addSource(source, {
        type: 'geojson',
        data: (visible && dataRef.current.mask) || EMPTY_GEOJSON,
      })
      map.addLayer(
        {
          id: layer,
          type: 'fill',
          source,
          paint: {
            'fill-color': MASK_COLOR,
            'fill-opacity': visible ? MASK_OPACITY : 0,
            'fill-opacity-transition': { duration: MASK_FADE_MS, delay: 0 },
          },
        },
        beforeLayer,
      )
    })
    map.addSource(OUTLINE_SOURCE_ID, {
      type: 'geojson',
      data: dataRef.current.area ?? EMPTY_GEOJSON,
    })
    map.addLayer(
      {
        id: OPERATING_AREA_OUTLINE_LAYER_ID,
        type: 'line',
        source: OUTLINE_SOURCE_ID,
        paint: {
          'line-color': OUTLINE_COLOR,
          'line-width': OUTLINE_WIDTH,
        },
        layout: { 'line-join': 'round' },
      },
      beforeLayer,
    )

    return () => removeLayers(map)
  }, [map, hasArea, insertBeforeLayer])

  useEffect(() => {
    if (!map || !area) return
    map.getSource<maplibregl.GeoJSONSource>(OUTLINE_SOURCE_ID)?.setData(area)
  }, [map, area])

  // A changed mask goes into the hidden layer, which fades in while the shown
  // one fades out, so the unshaded area grows and shrinks smoothly.
  useEffect(() => {
    if (!map || mask === shownMaskRef.current) return
    const shown = MASKS[visibleMaskRef.current]
    const hiddenIndex = 1 - visibleMaskRef.current
    const hidden = MASKS[hiddenIndex]
    const source = map.getSource<maplibregl.GeoJSONSource>(hidden.source)
    if (!source) return
    const generation = layersGenerationRef.current
    shownMaskRef.current = mask
    source.setData(mask ?? EMPTY_GEOJSON)
    return whenSourceRetiled(map, hidden.source, () => {
      // The layers may have been recreated meanwhile, already showing the mask.
      if (layersGenerationRef.current !== generation) return
      map.setPaintProperty(hidden.layer, 'fill-opacity', MASK_OPACITY)
      map.setPaintProperty(shown.layer, 'fill-opacity', 0)
      visibleMaskRef.current = hiddenIndex
    })
  }, [map, mask])
}
